import React, { useState, useEffect } from 'react';
import {
  Modal, Table, Button, Form,
} from 'react-bootstrap';
import PortalUsersService, {
  PORTAL_USER_KINDS, KIND_LABELS,
} from '../../../Services/PortalUsersService';
import OperatorService from '../../../Services/OperatorService';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import PortalAccessHandover from './PortalAccessHandover';
import PortalLoginCells from './PortalLoginCells';
import PortalTakeoverConfirm from './PortalTakeoverConfirm';
import { emailHeldBy } from '../../../Utils/portalLogins';

// Portal logins for one operator's people. This is where staff hand a driver
// access to the portal, and where they take it away again.
//
// The enrolment token returned by a grant lives in this component's state and
// nowhere else: the parent unmounts the modal when it closes, so the token dies
// with it. It is never written to storage and never logged.

const emptyForm = { kind: PORTAL_USER_KINDS.DRIVER, driver_id: '', email: '' };

function PortalUsersModal({ operator, show = false, handleClose }) {
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  // { token, expiresAt, user } — the one-shot hand-over screen.
  const [handover, setHandover] = useState(null);
  // The API's PORTAL_EMAIL_HELD details while the admin decides whether to
  // replace the revoked login that holds the address. Null the rest of the
  // time, which is what keeps the confirmation off the screen.
  const [held, setHeld] = useState(null);

  const fetchUsers = async () => {
    if (!operator?.Id) return;

    setLoading(true);
    try {
      setUsers(await PortalUsersService.list(operator.Id));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch portal users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && operator?.Id) {
      fetchUsers();
    }
  }, [show, operator]);

  // Drivers come from the screen's existing path (the same one the Drivers
  // modal uses), and only when the grant form actually needs them.
  const openGrantForm = async () => {
    setShowGrantForm(true);
    setValidationErrors([]);
    if (drivers.length || !operator?.Id) return;

    try {
      const response = await OperatorService.fetchDrivers(operator.Id);
      setDrivers(response.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch drivers');
    }
  };

  const closeGrantForm = () => {
    setShowGrantForm(false);
    setFormData(emptyForm);
    setValidationErrors([]);
    // No grant form, no grant waiting on a decision.
    setHeld(null);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // A driver who already has a live login must not get a second one.
  const grantedDriverIds = new Set(
    users.filter((u) => u.active && u.driverId).map((u) => String(u.driverId)),
  );
  const availableDrivers = drivers.filter(
    (d) => d.active === 1 && !grantedDriverIds.has(String(d.Id)),
  );

  const isDriverGrant = formData.kind === PORTAL_USER_KINDS.DRIVER;
  const canSubmit = Boolean(formData.email.trim()) && (!isDriverGrant || formData.driver_id);

  // The row as the API returned it, plus what only this screen knows (the
  // driver's phone, for the WhatsApp hand-over).
  const showHandover = (grant, extra = {}) => {
    setHandover({
      token: grant.token,
      expiresAt: grant.expiresAt,
      user: { ...grant.user, ...extra },
    });
  };

  /**
   * Grant, and — only after the admin has confirmed it — grant again with
   * `replaceExisting`, which takes an address a revoked login still holds.
   * The form is left alone on a refusal, so confirming re-sends the very
   * same driver and address.
   */
  const submitGrant = async (replaceExisting = false) => {
    setLoading(true);
    setValidationErrors([]);
    try {
      const driver = drivers.find((d) => String(d.Id) === String(formData.driver_id));
      const grant = await PortalUsersService.grant({
        operatorId: operator.Id,
        kind: formData.kind,
        // The driver's own Id, not the <select>'s string value.
        driverId: isDriverGrant ? driver?.Id : undefined,
        email: formData.email.trim(),
        replaceExisting,
      });

      setHeld(null);
      showHandover(grant, isDriverGrant
        ? { name: driver?.full_name, phone: driver?.phone_number }
        : {});
      closeGrantForm();
      await fetchUsers();
    } catch (err) {
      const heldBy = emailHeldBy(err);

      // Somebody's REVOKED login holds the address: ask, then re-submit.
      // An active one (`replaceable: false`) is not an offer — it is an
      // error saying it must be revoked first, and it says so itself.
      if (heldBy?.replaceable && !replaceExisting) {
        setHeld(heldBy);
      } else {
        setHeld(null);
        setError(err.response?.data?.message || 'Failed to grant portal access');
        if (err.response?.data?.errors) {
          setValidationErrors(err.response.data.errors);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGrant = () => submitGrant();

  // A lost phone or an expired link: mint a new token, kill the old one.
  const handleRegenerate = async (user) => {
    setLoading(true);
    try {
      showHandover(await PortalUsersService.regenerate(user.cognitoSub), user);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate a new link');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (user) => {
    const who = user.name || user.email || 'this person';
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Revoke portal access for ${who}? Their signed-in phone is signed out `
      + 'immediately and the portal stops working on it. Any outstanding '
      + 'enrolment link stops working too.',
    )) {
      return;
    }

    setLoading(true);
    try {
      await PortalUsersService.revoke(user.cognitoSub);
      setHandover(null);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to revoke portal access');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="div">
          <div className="h5 mb-0">
            {handover
              ? `Portal access — ${handover.user?.name || handover.user?.email}`
              : 'Portal users'}
          </div>
          <div className="small text-muted">{operator?.name}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        {/* The one confirmation every grant screen uses. Cancel leaves the
            form exactly as it was; nothing was sent, and nothing is sent
            now. */}
        <PortalTakeoverConfirm
          held={held}
          grantingKind={formData.kind}
          busy={loading}
          onConfirm={() => submitGrant(true)}
          onCancel={() => setHeld(null)}
        />

        {handover ? (
          <PortalAccessHandover
            user={handover.user}
            token={handover.token}
            expiresAt={handover.expiresAt}
            operatorName={operator?.name}
          />
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Who can sign in to the portal</h6>
              {!showGrantForm && (
                <Button variant="primary" size="sm" onClick={openGrantForm} disabled={loading}>
                  <i className="fa fa-key me-2" />
                  Grant access
                </Button>
              )}
            </div>

            {showGrantForm && (
              <div className="card mb-3 bg-light">
                <div className="card-body">
                  <h6 className="card-title">Grant portal access</h6>
                  <Form>
                    <div className="row">
                      <Form.Group className="mb-2 col-md-4" controlId="portal_kind">
                        <Form.Label>Kind *</Form.Label>
                        <Form.Select
                          value={formData.kind}
                          onChange={(e) => handleChange('kind', e.target.value)}
                          size="sm"
                        >
                          <option value={PORTAL_USER_KINDS.DRIVER}>
                            {KIND_LABELS[PORTAL_USER_KINDS.DRIVER]}
                          </option>
                          <option value={PORTAL_USER_KINDS.OPERATOR}>
                            {KIND_LABELS[PORTAL_USER_KINDS.OPERATOR]}
                          </option>
                        </Form.Select>
                      </Form.Group>

                      {isDriverGrant && (
                        <Form.Group className="mb-2 col-md-4" controlId="portal_driver_id">
                          <Form.Label>Driver *</Form.Label>
                          <Form.Select
                            value={formData.driver_id}
                            onChange={(e) => handleChange('driver_id', e.target.value)}
                            size="sm"
                          >
                            <option value="">Select a driver…</option>
                            {availableDrivers.map((driver) => (
                              <option key={driver.Id} value={driver.Id}>
                                {driver.full_name}
                              </option>
                            ))}
                          </Form.Select>
                          <FormControlFeedback errors={validationErrors} field="driver_id" />
                          {availableDrivers.length === 0 && (
                            <Form.Text className="text-muted">
                              Every active driver already has portal access.
                            </Form.Text>
                          )}
                        </Form.Group>
                      )}

                      <Form.Group className="mb-2 col-md-4" controlId="portal_email">
                        <Form.Label>E-mail address *</Form.Label>
                        <Form.Control
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          size="sm"
                        />
                        <FormControlFeedback errors={validationErrors} field="email" />
                      </Form.Group>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleGrant}
                        disabled={loading || !canSubmit}
                      >
                        <i className="fa fa-check me-2" />
                        Generate link
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={closeGrantForm}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            )}

            <Table striped bordered hover size="sm" responsive className="responsive-card-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kind</th>
                  <th>Status</th>
                  <th>Enrolment</th>
                  <th>Granted</th>
                  <th style={{ width: '170px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">
                      No portal users yet
                    </td>
                  </tr>
                )}
                {users.map((user) => (
                  <tr
                    key={user.cognitoSub}
                    className={user.active ? '' : 'text-muted'}
                  >
                    <td data-label="Name">
                      <div>{user.name || '-'}</div>
                      <div className="small text-muted">{user.email}</div>
                    </td>
                    <td data-label="Kind">{KIND_LABELS[user.kind] || user.kind}</td>
                    <PortalLoginCells
                      user={user}
                      busy={loading}
                      onRegenerate={handleRegenerate}
                      onRevoke={handleRevoke}
                    />
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className={handover ? 'justify-content-between' : ''}>
        {handover ? (
          <>
            <Button
              variant="outline-danger"
              onClick={() => handleRevoke(handover.user)}
              disabled={loading || !handover.user?.cognitoSub}
            >
              Revoke all access
            </Button>
            <Button variant="primary" onClick={() => setHandover(null)}>
              Done
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default PortalUsersModal;
